import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Col } from 'react-bootstrap';
import i18n from 'utils/I18n';
import CustomButton from 'component/base/CustomButton';
import { ProfileEditState, ProfileEditType } from 'utils/CustomEnums';
import GenderPicker from './GenderPicker';
import MonthPicker, { CustomDatePickerInput } from 'component/date_picker/MonthPicker';
import { getMyAccountUserBirthdayForDisplay, getMyAccountUserBirthdayForUpdate } from 'utils/DateTimeUtils';
import { createAction } from 'utils';

export const EditButton = ({ state, editClick }) => {
  const onClick = () => {
    if (state === ProfileEditState.Normal) {
      editClick(true);
    } else if (state === ProfileEditState.Editing) {
      editClick(false);
    }
  };
  return (
    <span
      className={
        state === ProfileEditState.Disable
          ? 'personal_edit_button inactive'
          : 'personal_edit_button'
      }
      onClick={onClick}
    >
      {state === ProfileEditState.Editing
        ? i18n.t('message_cancel')
        : i18n.t('message_edit')}
    </span>
  );
};

export const SingleLoginOrContactSection = ({
  show,
  contactValue,
  showEdit,
  showCustomerService,
  contactForDirectMarketingEditState,
  setEditType,
  showSecondLoginMethodModal,
  hideSecondLoginMethodModal,
  setShowCustomerContactModal,
}) => {
  if (!show) {
    return null;
  };
  return (
    <div className="contact-method-container subheading">
      <label className="field-text">{contactValue || '-'}</label>
      {showEdit && (
        <EditButton
          state={contactForDirectMarketingEditState}
          editClick={(startEdit) => {
            // show contact customer service popup
            if (showCustomerService) {
              setShowCustomerContactModal({
                show: true,
                isForSecondContact: false,
              });
              return;
            };
            // edit login or contact method in app
            if (startEdit) {
              setEditType(ProfileEditType.contactForDirectMarketing);
              showSecondLoginMethodModal();
            } else {
              setEditType(null);
              hideSecondLoginMethodModal();
            }
          }}
        />
      )}
    </div>
  );
};

export const FirstNameSection = ({
  show,
  mandatory,
  editable,
  firstNameEditState,
  setEditType,
  firstName,
  setFirstName,
  userInfo,
}) => {
  const dispatch = useDispatch();
  const saveButtonOnClick = () => {
    dispatch(createAction('userInfoTemp/updateUserInfoOneField')({
      serverKey: 'first_name',
      modelKey: 'firstName',
      value: firstName,
      successCallback: () => {
        setEditType(null);
      },
    }));
  };

  if (!show) {
    return null;
  };

  return (
    <Col className="common-account-col">
      <div className="common-account-item">
        <div className="common-item-title subheading">
          {i18n.t('personal_info_first_name')}
          {!mandatory && i18n.t("personal_info_optional")}
          {editable && (
            <EditButton
              state={firstNameEditState}
              editClick={(startEdit) => {
                if (startEdit) {
                  setEditType(ProfileEditType.FirstName);
                } else {
                  setEditType(null);
                }
              }}
            />
          )}
        </div>
        {firstNameEditState === ProfileEditState.Editing ? (
          <div className="common-item-value field-text">
            <input
              className="account-input-with-downarrow field-text"
              defaultValue={firstName}
              onChange={(e) => {
                const text = e.target.value;
                setFirstName(text);
              }}
            />
            <CustomButton
              className="account-input-save-button"
              disabled={!firstName}
              text={i18n.t('save')}
              btnClicked={saveButtonOnClick}
            />
          </div>
        ) : (
          <div className="common-item-value field-text">
            {userInfo.firstName || '-'}
          </div>
        )}
      </div>
    </Col>
  );
};

export const LastNameSection = ({
  show,
  mandatory,
  editable,
  lastNameEditState,
  setEditType,
  lastName,
  setLastName,
  userInfo,
}) => {
  const dispatch = useDispatch();
  const saveButtonOnClick = () => {
    dispatch(createAction('userInfoTemp/updateUserInfoOneField')({
      serverKey: 'last_name',
      modelKey: 'lastName',
      value: lastName,
      successCallback: () => {
        setEditType(null);
      },
    }));
  };

  if (!show) {
    return null;
  };

  return (
    <Col className="common-account-col">
      <div className="common-account-item">
        <div className="common-item-title subheading">
          {i18n.t('personal_info_last_name')}
          {!mandatory && i18n.t("personal_info_optional")}
          {editable && (
            <EditButton
              state={lastNameEditState}
              editClick={(startEdit) => {
                if (startEdit) {
                  setEditType(ProfileEditType.LastName);
                } else {
                  setEditType(null);
                }
              }}
            />
          )}
        </div>
        {lastNameEditState === ProfileEditState.Editing ? (
          <div className="common-item-value field-text">
            <input
              className="account-input-with-downarrow field-text"
              defaultValue={lastName}
              onChange={(e) => {
                const text = e.target.value;
                setLastName(text);
              }}
            />
            <CustomButton
              className="account-input-save-button"
              disabled={!lastName}
              text={i18n.t('save')}
              btnClicked={saveButtonOnClick}
            />
          </div>
        ) : (
          <div className="common-item-value field-text">
            {userInfo.lastName || '-'}
          </div>
        )}
      </div>
    </Col>
  );
};

export const GenderSection = ({
  show,
  mandatory,
  editable,
  genderEditState,
  setEditType,
  genderList,
  userInfo,
  selectedGender,
  setSelectedGender,
  displayGender,
}) => {
  const dispatch = useDispatch();
  const saveButtonOnClick = () => {
    dispatch(createAction('userInfoTemp/updateUserInfoOneField')({
      serverKey: 'gender',
      modelKey: 'gender',
      value: selectedGender,
      successCallback: () => {
        setEditType(null);
      },
    }));
  };

  if (!show) {
    return null;
  };

  return (
    <Col className="common-account-col">
      <div className="common-account-item">
        <div className="common-item-title subheading">
          {i18n.t('personal_info_gender')}
          {!mandatory && i18n.t('personal_info_optional')}
          {editable && (
            <EditButton
              state={genderEditState}
              editClick={(startEdit) => {
                if (startEdit) {
                  setEditType(ProfileEditType.Gender);
                } else {
                  setEditType(null);
                }
              }}
            />
          )}
        </div>
        {genderEditState === ProfileEditState.Editing ? (
          <div className="common-item-value field-text gender">
            <GenderPicker
              customClass={'gender-picker-box'}
              sections={genderList}
              value={userInfo.gender}
              selectValue={(value) => {
                setSelectedGender(value);
              }}
            />
            <CustomButton
              className="account-input-save-button"
              disabled={!selectedGender}
              text={i18n.t('save')}
              btnClicked={saveButtonOnClick}
            />
          </div>
        ) : (
          <div className="common-item-value field-text">{displayGender}</div>
        )}
      </div>
    </Col>
  );
};

export const BirthdaySection = ({
  language,
  show,
  mandatory,
  editable,
  birthdayEditState,
  setEditType,
  userInfo,
  birthday,
  setBirthday,
}) => {
  const dispatch = useDispatch();
  const [showBirthMonthOptions, setShowBirthMonthOptions] = useState(false);
  const saveButtonOnClick = () => {
    dispatch(createAction('userInfoTemp/updateUserInfoOneField')({
      serverKey: 'date_of_birth',
      modelKey: 'birthday',
      value: getMyAccountUserBirthdayForUpdate(birthday),
      successCallback: () => {
        setEditType(null);
      },
    }));
  };

  if (!show) {
    return null;
  };

  return (
    <Col className="common-account-col">
      <div className="common-account-item">
        <div className="common-item-title subheading">
          {i18n.t('account_birth_month_year')}
          {!mandatory && i18n.t("personal_info_optional")}
          {editable && (
            <EditButton
              state={birthdayEditState}
              editClick={(startEdit) => {
                if (startEdit) {
                  setEditType(ProfileEditType.Birthday);
                } else {
                  setEditType(null);
                }
              }}
            />
          )}
        </div>
        {birthdayEditState !== ProfileEditState.Editing ? (
          <div className="common-item-value field-text">
            {userInfo.birthday
              ? getMyAccountUserBirthdayForDisplay(userInfo.birthday)
              : i18n.t('not_provided')}
          </div>
        ) : (
          <div className="common-item-value field-text">
            <CustomDatePickerInput
              value={
                birthday ? getMyAccountUserBirthdayForDisplay(birthday) : null
              }
              onClick={() => {
                setShowBirthMonthOptions(!showBirthMonthOptions);
              }}
              showBirthMonthOptions={showBirthMonthOptions}
            />
            <MonthPicker
              show={showBirthMonthOptions}
              lang={language}
              selected={birthday}
              onChange={(date) => {
                if (!!date) {
                  setBirthday(date);
                }
                setShowBirthMonthOptions(false);
              }}
            />
            <CustomButton
              className="account-input-save-button"
              disabled={!birthday}
              text={i18n.t('save')}
              btnClicked={saveButtonOnClick}
            />
          </div>
        )}
      </div>
    </Col>
  );
};
